import React from "react";
const Avatar = ({
  src,
  alt,
  size = "md",
  className = "",
  outline = false,
  showInitials = false,
  color = "primary", // primary, warning, success, danger, info, black, white
  colorDepth = "500", // 50,100,200,300,400,500,600,700,800,900
  variant = "contained", // contained, outlined
  noBorder = false,
}) => {
  const sizeClasses = {
    xs: "w-5 h-5 text-[8px]",
    "xs-sm": "w-6 h-6 text-[10px]",
    sm: "w-7 h-7 text-xs",
    md: "w-8 h-8 text-sm",
    lg: "w-9 h-9 text-base",
    xl: "w-12 h-12 text-xl",
    xxl: "w-[52px] h-[52px] text-2xl",
    "3xl": "w-[64px] h-[64px] text-3xl",
    "4xl": "w-[80px] h-[80px] text-4xl",
    "5xl": "w-[120px] h-[120px] text-5xl",
  };

  const borderClasses = {
    xs: "border-[1px]",
    "xs-sm": "border-[1px]",
    sm: "border-[1px]",
    md: "border-[1px]",
    lg: "border-[2px]",
    xl: "border-[2px]",
    xxl: "border-[2px]",
    "3xl": "border-[2px]",
    "4xl": "border-[2px]",
    "5xl": "border-[2px]",
  };

  const getNextColorDepth = (currentDepth) => {
    const depths = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900];
    const currentIndex = depths.indexOf(Number(currentDepth));

    if (currentIndex === -1) return "500"; // fallback

    // If 900 or 800, return 900
    if (["800", "900"].includes(String(currentDepth))) {
      return "900";
    }

    // Jump two steps ahead, clamp to last index (900)
    const nextIndex = Math.min(currentIndex + 2, depths.length - 1);
    return String(depths[nextIndex]);
  };

  const getTextColor = (depth) => {
    return Number(depth) < 400 ? "black" : "white";
  };

  const updateColorDepth = (variant, colorDepth) => {
    return variant === "outlined" ? "50" : colorDepth;
  };

  const updatedColorDepth = updateColorDepth(variant, colorDepth);

  const getInitials = (name) => {
    if (!name || typeof name !== "string") return "A";

    const words = name
      .trim()
      .split(/\s+/)
      .filter((word) => word.length > 0);

    if (words.length === 0) return "A";
    if (words.length === 1) return words[0].charAt(0).toUpperCase();

    const firstInitial = words[0].charAt(0).toUpperCase();
    const lastInitial = words[words.length - 1].charAt(0).toUpperCase();

    return firstInitial + lastInitial;
  };

  const getDisplayText = () => {
    if (showInitials && alt) {
      return getInitials(alt);
    }
    return alt?.charAt(0).toUpperCase() || "A";
  };

  const borderColorDepth = noBorder
    ? colorDepth
    : getNextColorDepth(colorDepth);

  return (
    <div
      className={`select-none flex items-center justify-center rounded-full font-normal overflow-hidden uppercase font-poppins aspect-square flex-shrink-0 ${
        borderClasses[size]
      } border-${color}-${borderColorDepth} leading-none ${
        sizeClasses[size]
      } ${className} ${
        !outline
          ? `bg-${color}-${updatedColorDepth} text-${getTextColor(
              updatedColorDepth,
            )}`
          : "text-neutrals-500"
      }`}
    >
      {src ? (
        <img src={src} alt={alt} className="w-full h-full object-cover" />
      ) : (
        getDisplayText()
      )}
    </div>
  );
};

export default Avatar;
